"use client";

import posthog from "posthog-js";
import { isPostHogBrowserCaptureEnabled } from "./posthog-environment";
import { isPostHogSuppressedPath } from "./posthog-suppressed-path";
import {
	POSTHOG_UTM_CONTEXT_FIELDS,
	sanitizePostHogEventContext,
	type PostHogEventContext,
} from "./posthog-event-context";

/** Analytics must never interrupt a customer action. */
export function captureStorefrontEvent(event: string, properties: Record<string, unknown>): void {
	try {
		if (
			!isPostHogBrowserCaptureEnabled({
				hostname: typeof window === "undefined" ? undefined : window.location.hostname,
			})
		)
			return;
		if (!posthog.__loaded || posthog.has_opted_out_capturing()) return;
		if (isPostHogSuppressedPath(window.location.pathname)) return;
		posthog.capture(event, properties);
	} catch {
		// An unavailable analytics SDK must not break the shop.
	}
}

export function getPostHogEventContext(): PostHogEventContext {
	try {
		if (
			!isPostHogBrowserCaptureEnabled({
				hostname: typeof window === "undefined" ? undefined : window.location.hostname,
			})
		)
			return { disabled: true };
		if (typeof window !== "undefined" && isPostHogSuppressedPath(window.location.pathname))
			return { disabled: true };
		if (!posthog.__loaded || posthog.has_opted_out_capturing()) return { disabled: true };
		const campaigns = Object.fromEntries(
			Object.entries(POSTHOG_UTM_CONTEXT_FIELDS).map(([field, property]) => [
				field,
				posthog.get_property(`first_touch_${property}`) ?? posthog.get_property(`$initial_${property}`),
			]),
		);
		return sanitizePostHogEventContext({
			distinctId: posthog.get_distinct_id(),
			sessionId: posthog.get_session_id(),
			currentUrl: typeof window === "undefined" ? undefined : window.location.href,
			referrer: typeof document === "undefined" ? undefined : document.referrer,
			userAgent: typeof navigator === "undefined" ? undefined : navigator.userAgent,
			...campaigns,
		});
	} catch {
		return { disabled: true };
	}
}
